import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTable, useCustom } from '@refinedev/core';
import { StatCard } from '@/components/ui/stat-card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { ListPage, EditButton, ShowButton, DeleteButton } from '@/components/crud';
import { Building2, Mail, Phone, CheckCircle2, Briefcase, CalendarPlus } from "lucide-react";
import { PageHeader } from '@/components/ui/page-header';
import { rowOpenProps } from '@/lib/rowOpen';
import { ExportDropdown } from '../../components/common/ExportDropdown';
import { exportRich, type RichFormat } from '../../utils/richExport';

interface Branch {
  id: number;
  code: string;
  name: string;
  description?: string;
  company: number;
  company_name: string;
  email: string;
  phone: string;
  manager?: number;
  manager_name?: string;
  status: 'active' | 'inactive' | 'maintenance';
  settings: any;
  created_by_name: string;
  updated_by_name: string;
  created_at: string;
  updated_at: string;
  device_count: number;
}

export const BranchManagementList: React.FC = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');

  const { tableQuery } = useTable<Branch>({
    resource: 'branches',
    sorters: { initial: [{ field: 'company_name', order: 'asc' }, { field: 'name', order: 'asc' }] },
  });

  const statsQuery = useCustom({ url: '/core/branches/statistics/', method: 'get' });
  const statisticsData: any = statsQuery.data?.data || {};
  const records = tableQuery?.data?.data || [];

  const getStatusVariant = (status: string) => {
    const map: Record<string, "success" | "secondary" | "warning"> = { active: "success", inactive: "secondary", maintenance: "warning" };
    return map[status] || "secondary";
  };

  // The rows the screen is actually showing — the export must match the search.
  const visible = records.filter((r) => !search
    || r.name.toLowerCase().includes(search.toLowerCase())
    || r.company_name?.toLowerCase().includes(search.toLowerCase()));

  const exportBranches = async (format: RichFormat) => {
    await exportRich(format, {
      fileName: 'branches',
      title: 'Branches',
      subtitle: 'Branch network, managers and contact details',
      meta: search ? { Search: `"${search}"` } : undefined,
      columns: [
        'Code', { header: 'Branch', width: 28 }, 'Company', 'Manager',
        { header: 'Email', width: 28 }, 'Phone', { header: 'Status', kind: 'status' },
        'Devices', 'Created',
      ],
      rows: visible.map((r) => [
        r.code, r.name, r.company_name || '', r.manager_name || '',
        r.email || '', r.phone || '',
        r.status ? r.status.charAt(0).toUpperCase() + r.status.slice(1) : '',
        r.device_count ?? 0, r.created_at || '',
      ]),
    });
  };

  return (
    <div>
      <PageHeader title="Branch Management" icon={<Building2 className="h-6 w-6" />} />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 mb-6">
        <StatCard label="Total Branches" value={statisticsData.total_branches || 0} icon={Building2} tone="brand" />
        <StatCard label="Active Branches" value={statisticsData.active_branches || 0} icon={CheckCircle2} tone="success" />
        <StatCard label="Companies" value={Object.keys(statisticsData.branches_by_company || {}).length} icon={Briefcase} tone="brand" />
        <StatCard label="Recent (this month)" value={statisticsData.recent_branches || 0} icon={CalendarPlus} tone="brand" />
      </div>

      <ListPage title="Branches">
        <div className="flex items-center gap-4 mb-4">
          <Input placeholder="Search branches..." value={search} onChange={(e) => setSearch(e.target.value)} className="max-w-sm" />
          <div className="flex gap-2 ml-auto">
            <ExportDropdown size="default" buttonText="Export List" options={{ data: visible, filename: 'branches', formats: ['excel', 'pdf', 'csv', 'json'], customExportHandler: (f) => exportBranches(f as RichFormat) }} />
            <Button variant="outline">Import Branches</Button>
          </div>
        </div>

        <div className="rounded-md border">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="p-3 text-left font-medium">Branch</th>
                <th className="p-3 text-left font-medium">Manager</th>
                <th className="p-3 text-left font-medium">Contact</th>
                <th className="p-3 text-left font-medium">Status</th>
                <th className="p-3 text-left font-medium">Devices</th>
                <th className="p-3 text-left font-medium">Updated</th>
                <th className="p-3 text-right font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((record) => (
                <tr key={record.id} {...rowOpenProps(() => navigate(`/core/branches/edit/${record.id}`), 'border-b hover:bg-muted/50')}>
                  <td className="p-3">
                    <div>
                      <p className="font-medium">{record.name}</p>
                      <p className="text-xs text-muted-foreground">Code: {record.code}</p>
                      <p className="text-xs text-info">{record.company_name}</p>
                    </div>
                  </td>
                  <td className="p-3 text-sm">{record.manager_name || <span className="text-muted-foreground">None</span>}</td>
                  <td className="p-3">
                    <div className="space-y-1">
                      <p className="text-xs flex items-center gap-1"><Mail className="h-3 w-3 text-info" />{record.email}</p>
                      <p className="text-xs flex items-center gap-1"><Phone className="h-3 w-3 text-success-700" />{record.phone}</p>
                    </div>
                  </td>
                  <td className="p-3"><Badge variant={getStatusVariant(record.status)}>{record.status}</Badge></td>
                  <td className="p-3 text-center font-medium text-info">{record.device_count}</td>
                  <td className="p-3">
                    <p className="text-xs">{new Date(record.updated_at).toLocaleDateString()}</p>
                    <p className="text-xs text-muted-foreground">by {record.updated_by_name || record.created_by_name}</p>
                  </td>
                  <td className="p-3">
                    <div className="flex items-center justify-end gap-1">
                      <ShowButton recordItemId={record.id} hideText size="icon" />
                      <EditButton recordItemId={record.id} hideText size="icon" />
                      <DeleteButton recordItemId={record.id} hideText size="icon" />
                    </div>
                  </td>
                </tr>
              ))}
              {records.length === 0 && (
                <tr><td colSpan={7} className="p-8 text-center text-muted-foreground">No branches found</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </ListPage>
    </div>
  );
};

export default BranchManagementList;
